import withVerticalScroller from '../../components/withVerticalScroller';
import { Channel } from '..';
import { useChannel } from '../../contexts/Channel';
import { ErrorIcon } from '../../assets/icons';
import Spinner from '../../components/Spinner';
import PropTypes from 'prop-types';
import BannerImage from '../../assets/banner/ubex-poster.png';
import { useCallback } from 'react';

const NotLiveContent = () => {
  return (
    <div className="w-full h-full">
      <img
        src={BannerImage}
        alt="banner-image"
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const LoadingContent = () => (
  <div className="flex flex-col items-center justify-center dark:text-white w-full h-full">
    <Spinner />
    <p className="font-semibold">Loading...</p>
  </div>
);

const ErrorContent = ({ message = 'Failed to load stream' }) => (
  <div className="flex flex-col items-center dark:text-white w-full h-full justify-center">
    <div className="w-10 mb-4">
      <ErrorIcon className="w-full h-full fill-white" />
    </div>
    <p className="font-semibold">{message}</p>
    <p className="text-wrap sm:text-sm lg:text-lg">
      Please try again in a few moments.
    </p>
  </div>
);

ErrorContent.propTypes = {
  message: PropTypes.string
};

const ChannelStreamView = () => {
  const { channelData } = useChannel();
  const {
    isLive,
    isIvsPlayerReady,
    ivsPlayerErrorType,
    isTokenLoading,
    tokenLoadingTimeout,
    tokenLoadingError
  } = channelData || {};

  const getLoadingMessage = useCallback(() => {
    if (tokenLoadingTimeout) {
      return (
        <ErrorContent message="We're having trouble connecting to the stream." />
      );
    }

    if (tokenLoadingError) {
      return <ErrorContent message={tokenLoadingError} />;
    }

    if (isTokenLoading) {
      return <LoadingContent />;
    }

    // Handle network or initialization errors first
    if (ivsPlayerErrorType !== 'NoError') {
      switch (ivsPlayerErrorType) {
        case 'ErrorNotAvailable':
        case 'StreamEnded':
        case 'ErrorAuthorization':
          return <NotLiveContent />;
        default:
          return (
            <ErrorContent message="We're having trouble connecting to the stream." />
          );
      }
    }

    // Handle stream status
    if (!isLive) {
      return <NotLiveContent />;
    }

    // Handle player ready state
    if (!isIvsPlayerReady) {
      return <LoadingContent />;
    }

    return null;
  }, [
    ivsPlayerErrorType,
    isIvsPlayerReady,
    isLive,
    isTokenLoading,
    tokenLoadingTimeout,
    tokenLoadingError
  ]);

  const LoadingOverlay = () => (
    <div className="absolute inset-0 bg-white flex justify-center items-center z-[1000] dark:bg-black w-full h-full">
      <div className="text-black dark:text-white text-lg w-full h-full">
        {getLoadingMessage()}
      </div>
    </div>
  );

  const shouldShowOverlay =
    ivsPlayerErrorType !== 'NoError' || !isIvsPlayerReady || !isLive;

  return (
    <div className="relative">
      {shouldShowOverlay && <LoadingOverlay />}
      <Channel />
    </div>
  );
};

export default withVerticalScroller(ChannelStreamView);
